import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExperiment } from '../../contexts/ExperimentContext';
import { useTracker } from '../../contexts/TrackerContext';
import './Instructions.css';

function Instructions() {
  const navigate = useNavigate();
  const { experimentData } = useExperiment();
  const { tracker } = useTracker();
  
  useEffect(() => {
    tracker.track('phaseStart', { phase: 'instructions' });
  }, [tracker]);

  const handleContinue = () => {
    tracker.track('instructionsComplete', {
      duration: 0 // Will be calculated from phase start time in backend
    });

    // Navigate based on two-task cohort assignment
    const taskAssignment = experimentData?.participant?.taskAssignment;
    const primaryTask = taskAssignment?.primaryTask;
    const secondaryTask = taskAssignment?.secondaryTask;
    const taskOrder = taskAssignment?.taskOrder;

    console.log('=== NAVIGATION DEBUG - handleContinue ===');
    console.log('Full experimentData:', JSON.stringify(experimentData, null, 2));
    console.log('taskAssignment object:', taskAssignment);
    console.log('primaryTask:', primaryTask);
    console.log('secondaryTask:', secondaryTask);
    console.log('taskOrder:', taskOrder);
    console.log('cohort:', taskAssignment?.cohort);

    // FIXED: Determine first task based on taskOrder
    let firstTask = primaryTask;
    if (taskOrder === 'secondary-first') {
      firstTask = secondaryTask;
      console.log(`✓ Task order is secondary-first, routing to secondary task: ${secondaryTask}`);
    } else {
      console.log(`✓ Task order is email-first (or default), routing to primary task: ${primaryTask}`);
    }

    console.log('Determined firstTask:', firstTask);
    console.log('About to switch on firstTask value...');

    // Check if this is an isolated task run (e.g., Prolific info-sharing only)
    const isolatedTask = localStorage.getItem('selectedTask');
    if (isolatedTask === 'infosharing') {
      console.log('→ Isolated task: Navigating to /info-sharing-intro');
      navigate('/info-sharing-intro');
      return;
    }

    // Route to first assigned task intro page
    switch (firstTask) {
      case 'email':
        console.log('→ Navigating to /email-task-intro');
        navigate('/email-task-intro');
        break;
      case 'device':
        console.log('→ Navigating to /device-task-intro');
        navigate('/device-task-intro');
        break;
      case 'coding':
        console.log('→ Navigating to /coding-task-intro');
        navigate('/coding-task-intro');
        break;
      default:
        console.log('⚠️ Hit default case - firstTask was:', firstTask);
        // Fallback to old behavior for existing participants
        const oldTaskOrder = experimentData?.participant?.conditions?.taskOrder;
        console.log('Checking old conditions.taskOrder:', oldTaskOrder);
        if (oldTaskOrder === 'device-first') {
          console.log('→ Fallback: Navigating to /device-task-intro');
          navigate('/device-task-intro');
        } else {
          console.log('→ Fallback: Navigating to /email-task-intro');
          navigate('/email-task-intro');
        }
    }
    console.log('=== END NAVIGATION DEBUG ===');
  };

  // Prior experience manipulation
  const renderExperienceManipulation = () => {
    if (experimentData?.participant?.conditions?.priorExperience === 'simulated') {
      return (
        <div className="experience-manipulation">
          <h3>Important Security Information</h3>
          <div className="incident-example">
            <p><strong>Recent Incident Example:</strong></p>
            <p>Last month, an employee clicked on what appeared to be a legitimate email from "IT Support" asking them to verify their password. The link led to a fake website that captured their credentials.</p>
            <p><strong>Consequences:</strong></p>
            <ul>
              <li>Unauthorized access to company systems</li>
              <li>Potential data breach affecting customer information</li>
              <li>Employee faced disciplinary action</li>
              <li>Company incurred significant recovery costs</li>
            </ul>
            <p className="warning-text">This demonstrates how sophisticated phishing attempts can be and why vigilance is crucial.</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Condition-specific instructions - FTT manipulations only
  const renderConditionInstructions = () => {
    const conditions = experimentData?.participant?.conditions;
    
    if (!conditions) return null;
    
    const { timePressure } = conditions;
    
    return (
      <>
        {timePressure === 'high' && (
          <div className="time-warning">
            <h3>⏱️ Time Constraint</h3>
            <p>Due to urgent business needs, you will have <strong>limited time</strong> to complete each task.</p>
            <p>A timer will be visible showing your remaining time. Please work efficiently but maintain accuracy.</p>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="instructions-phase">
      <div className="instructions-content">
        <h2>Study Instructions</h2>
        
        <div className="general-instructions">
          <p><strong>SCENARIO:</strong> You are at work, sitting at your computer. You will handle work emails and technology exactly as you would in your real job.</p>
          
          <h3>What You Will Do:</h3>
          {(() => {
            const taskAssignment = experimentData?.participant?.taskAssignment;
            const cohort = taskAssignment?.cohort;
            
            // Enhanced debug logging
            console.log('=== INSTRUCTIONS DEBUG ===');
            console.log('Instructions - experimentData:', experimentData);
            console.log('Instructions - full participant object:', experimentData?.participant);
            console.log('Instructions - taskAssignment:', taskAssignment);
            console.log('Instructions - cohort:', cohort);
            console.log('Instructions - localStorage experimentSession:', localStorage.getItem('experimentSession'));
            console.log('=== END DEBUG ===');
            
            if (cohort === 'email-device') {
              return (
                <div>
                  <div className="task-box">
                    <h4>📧 Handle Your Work Emails</h4>
                    <p>Imagine these simulated messages are in <strong>your work inbox</strong>. Handle them like you normally would.</p>
                  </div>
                  
                  <div className="task-box">
                    <h4>💻 Handle Workplace Technology</h4>
                    <p>Imagine these simulated situations occur at <strong>your workplace</strong>. Handle them like you normally would.</p>
                  </div>
                </div>
              );
            } else if (cohort === 'email-coding') {
              return (
                <div>
                  <div className="task-box">
                    <h4>📧 Handle Your Work Emails</h4>
                    <p>Imagine these simulated messages are in <strong>your work inbox</strong>. Handle them like you normally would.</p>
                  </div>
                  
                  <div className="task-box">
                    <h4>💻 Review Code for Your Projects</h4>
                    <p>Pretend you need this code for <strong>YOUR current work projects</strong>. Make decisions like you normally would.</p>
                  </div>
                </div>
              );
            } else {
              // Fallback for unassigned participants
              return (
                <div>
                  <div className="task-box">
                    <h4>📧 Handle Your Work Emails</h4>
                    <p>Imagine these simulated messages are in <strong>your work inbox</strong>. Handle them like you normally would.</p>
                  </div>
                  
                  <div className="task-box">
                    <h4>💻 Handle Workplace Security</h4>
                    <p>Imagine these simulated situations occur at <strong>your workplace</strong>. Handle them like you normally would.</p>
                  </div>
                </div>
              );
            }
          })()}

          <div className="important-note">
            <h3>🎯 KEY POINT:</h3>
            <p><strong>Act exactly like you're at your real job.</strong></p>
            <p>Use your normal work judgment. Make the same decisions you would make in your actual workplace.</p>
            <p>You will not receive a score during the tasks. Choose the action you would actually take.</p>
          </div>
        </div>

        {renderExperienceManipulation()}
        {renderConditionInstructions()}

        <div style={{ marginTop: '2rem', textAlign: 'center', paddingBottom: '2rem' }}>
          <button className="btn btn-primary" onClick={handleContinue}>
            Continue to First Task
          </button>
        </div>
      </div>
    </div>
  );
}

export default Instructions;
